import React from 'react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faListCheck,
  faArrowTrendUp,
  // faNewspaper, // Track chapter paused
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CHAPTERS } from './hero-device/heroTheaterData';
import { useHeroTheaterTimeline } from './hero-device/useHeroTheaterTimeline';
import DeviceFrame from './hero-device/DeviceFrame';
import HeroProductTheater from './hero-device/HeroProductTheater';

const CHAPTER_ICONS: Record<string, IconDefinition> = {
  discover: faMagnifyingGlass,
  evaluate: faListCheck,
  forecast: faArrowTrendUp,
  // track: faNewspaper, // Track chapter paused — restore with CHAPTERS entry
};

/** Circumference of the active-chapter progress ring (r=18.5). */
const CHAPTER_ARC_C = 2 * Math.PI * 18.5;

/**
 * Hero device theater shell: journey chapter rail + premium device + stylized scenes.
 * Does not alter surrounding hero copy.
 */
export default function HeroCarouselSection() {
  const {
    chapterIndex,
    chapterId,
    progress,
    reducedMotion,
    jumpToChapter,
    containerRef,
  } = useHeroTheaterTimeline();

  const chapterCount = CHAPTERS.length;
  // Continuous journey fill: lands on each node center as that chapter completes.
  const journeyFill =
    chapterCount <= 1
      ? 1
      : (chapterIndex + progress) / (chapterCount - 1);

  return (
    <div
      ref={containerRef}
      className="relative mx-auto w-full min-w-0 max-w-[95vw] lg:mx-0 lg:max-w-none"
    >
      <motion.div
        className="flex w-full flex-col items-center justify-center px-0"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        aria-roledescription="carousel"
        aria-label="MDFYI research journey"
      >
        {/* Chapter journey rail */}
        <div className="relative z-10 mb-5 w-full max-w-[min(100%,22rem)] sm:mb-6 sm:max-w-[26rem] md:mb-7 md:max-w-[28rem]">
          <div
            className="relative"
            role="tablist"
            aria-label="Workflow pillars"
          >
            {/* Connector track (behind nodes) — inset to node centers */}
            <div
              className="pointer-events-none absolute top-[1.15rem] h-[3px] overflow-hidden rounded-full sm:top-[1.3rem]"
              style={{
                left: `${50 / chapterCount}%`,
                right: `${50 / chapterCount}%`,
              }}
              aria-hidden
            >
              <div className="absolute inset-0 bg-black/[0.06]" />
              <motion.div
                className="absolute inset-y-0 left-0 origin-left rounded-full"
                style={{
                  background:
                    'linear-gradient(90deg, #34d399 0%, #059669 55%, #047857 100%)',
                  boxShadow: '0 0 12px rgba(5,150,105,0.45)',
                }}
                initial={false}
                animate={{
                  width: `${Math.min(1, Math.max(0, journeyFill)) * 100}%`,
                }}
                transition={
                  reducedMotion
                    ? { duration: 0 }
                    : { duration: 0.1, ease: 'linear' }
                }
              >
                {!reducedMotion && (
                  <motion.span
                    className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/55 to-transparent"
                    animate={{ x: ['-120%', '320%'] }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                )}
              </motion.div>
            </div>

            <div
              className="relative grid gap-1"
              style={{ gridTemplateColumns: `repeat(${chapterCount}, minmax(0, 1fr))` }}
            >
              {CHAPTERS.map((chapter, index) => {
                const isActive = index === chapterIndex;
                const isComplete = index < chapterIndex;
                const isUpcoming = index > chapterIndex;

                return (
                  <button
                    key={chapter.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`hero-theater-panel-${chapter.id}`}
                    id={`hero-theater-tab-${chapter.id}`}
                    onClick={() => jumpToChapter(index)}
                    className="group relative flex flex-col items-center gap-1.5 rounded-xl px-0.5 py-0.5 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcfcf9] sm:gap-2"
                  >
                    {/* Node */}
                    <span className="relative flex h-9 w-9 items-center justify-center sm:h-10 sm:w-10">
                      <motion.span
                        className="relative z-10 flex h-full w-full items-center justify-center rounded-full"
                        initial={false}
                        animate={
                          isActive
                            ? {
                                scale: 1.08,
                                // Same gradient + shadow shape in every state so Motion can mix.
                                background:
                                  'linear-gradient(145deg, rgba(16,185,129,1) 0%, rgba(5,150,105,1) 48%, rgba(4,120,87,1) 100%)',
                                boxShadow:
                                  '0 1px 0 rgba(255,255,255,0.28) inset, 0 0 0 1px rgba(6,95,70,0.35), 0 0 0 3px rgba(16,185,129,0.22), 0 0 14px 2px rgba(16,185,129,0.4)',
                                color: '#ffffff',
                              }
                            : isComplete
                              ? {
                                  scale: 1,
                                  background:
                                    'linear-gradient(145deg, rgba(240,253,246,1) 0%, rgba(240,253,246,1) 48%, rgba(240,253,246,1) 100%)',
                                  boxShadow:
                                    '0 0 0 rgba(255,255,255,0) inset, 0 0 0 1.5px rgba(5,150,105,0.34), 0 0 0 0px rgba(16,185,129,0), 0 2px 5px -2px rgba(5,150,105,0.14)',
                                  color: '#059669',
                                }
                              : {
                                  scale: 1,
                                  background:
                                    'linear-gradient(145deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.85) 48%, rgba(255,255,255,0.85) 100%)',
                                  boxShadow:
                                    '0 0 0 rgba(255,255,255,0) inset, 0 0 0 1px rgba(15,23,42,0.08), 0 0 0 0px rgba(16,185,129,0), 0 2px 6px -2px rgba(15,23,42,0.08)',
                                  color: '#94a3b8',
                                }
                        }
                        whileHover={
                          reducedMotion || isActive
                            ? undefined
                            : {
                                scale: 1.06,
                                color: '#334155',
                                boxShadow:
                                  '0 0 0 rgba(255,255,255,0) inset, 0 0 0 1px rgba(5,150,105,0.28), 0 0 0 0px rgba(16,185,129,0), 0 4px 10px -3px rgba(5,150,105,0.18)',
                              }
                        }
                        whileTap={
                          reducedMotion
                            ? undefined
                            : { scale: 0.94 }
                        }
                        transition={
                          reducedMotion
                            ? { duration: 0 }
                            : { type: 'spring', stiffness: 420, damping: 26, mass: 0.7 }
                        }
                      >
                        <FontAwesomeIcon
                          icon={CHAPTER_ICONS[chapter.id]}
                          className="block h-[0.95em] w-[0.95em]"
                          aria-hidden
                        />

                        {/* Chapter progress arc on active node */}
                        {isActive && !reducedMotion && (
                          <svg
                            className="pointer-events-none absolute inset-[-3px] h-[calc(100%+6px)] w-[calc(100%+6px)] -rotate-90"
                            viewBox="0 0 40 40"
                            aria-hidden
                          >
                            <circle
                              cx="20"
                              cy="20"
                              r="18.5"
                              fill="none"
                              stroke="rgba(255,255,255,0.22)"
                              strokeWidth="2"
                            />
                            <circle
                              cx="20"
                              cy="20"
                              r="18.5"
                              fill="none"
                              stroke="rgba(255,255,255,0.92)"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeDasharray={CHAPTER_ARC_C}
                              strokeDashoffset={
                                CHAPTER_ARC_C * (1 - Math.min(1, Math.max(0, progress)))
                              }
                            />
                          </svg>
                        )}
                      </motion.span>
                    </span>

                    {/* Label */}
                    <motion.span
                      className="relative text-[12px] font-semibold leading-none tracking-[0.01em] sm:text-[14px]"
                      initial={false}
                      animate={{
                        color: isActive
                          ? '#064e3b'
                          : isComplete
                            ? '#059669'
                            : '#94a3b8',
                        y: isActive ? 0 : 1,
                        opacity: isUpcoming ? 0.72 : 1,
                      }}
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : { type: 'spring', stiffness: 380, damping: 28 }
                      }
                    >
                      {chapter.title}
                      {isActive && (
                        <motion.span
                          layoutId="hero-chapter-underline"
                          className="absolute inset-x-0 -bottom-1 h-[2px] rounded-full bg-emerald-600"
                          transition={
                            reducedMotion
                              ? { duration: 0 }
                              : { type: 'spring', stiffness: 400, damping: 28 }
                          }
                        />
                      )}
                    </motion.span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Device */}
        <div className="relative z-0 flex w-full justify-center">
          <div
            id={`hero-theater-panel-${chapterId}`}
            role="tabpanel"
            aria-labelledby={`hero-theater-tab-${chapterId}`}
          >
            <DeviceFrame>
              <HeroProductTheater
                chapterId={chapterId}
                progress={progress}
                reducedMotion={reducedMotion}
              />
            </DeviceFrame>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
